import React, {forwardRef, useEffect, useImperativeHandle, useMemo} from "react";
import {Box, CircularProgress, Divider, TextField, Typography} from "@mui/material";
import {CreateExampleSentence, UpdateExampleSentence} from "../../types/word_operations";
import {AudioParamsView, AudioType} from "./AudioParamsView";
import {FormValidationHandle} from "./WordInfoEditView";
import {useDeck} from "../../../deck-management/context/DeckContext";

export interface ExampleSentenceEditProps {
    existingAudioPath?: string | null
    sentencesConfig: CreateExampleSentence[] | UpdateExampleSentence[]
    onChange: (updatedSentence: CreateExampleSentence[] | UpdateExampleSentence[]) => void
    isLoading: boolean
}

export const ExampleSentenceEditView = forwardRef<FormValidationHandle, ExampleSentenceEditProps>(({existingAudioPath, sentencesConfig, onChange, isLoading}, ref) => {

    const [sentenceValidationError, setSentenceValidationError] = React.useState<string | null>(null);
    const [translationValidationError, setTranslationValidationError] = React.useState<string | null>(null);

    const {currentDeck} = useDeck();

    useImperativeHandle(ref, () => ({
        validateForm
    }));


    const starFreeText = useMemo(() => {
        return sentencesConfig[0]?.sentence?.split("*")?.join("") || "";
    }, [sentencesConfig]);

    const language = useMemo(() => {
        return currentDeck!.language;
    }, []);

    const validateForm = (): boolean => {
        const isSentenceProvided = !!starFreeText && starFreeText.trim().length > 0;
        const isTranslationProvided = !!sentencesConfig?.[0]?.translation && sentencesConfig?.[0]?.translation?.trim().length > 0;
        setSentenceValidationError(!isSentenceProvided && isTranslationProvided ? `${language} sentence not provided` : null);
        setTranslationValidationError(!isTranslationProvided && isSentenceProvided ? `Translation not provided` : null);
        return isSentenceProvided === isTranslationProvided;
    }

    useEffect(() => {
        if (!sentenceValidationError && !translationValidationError) {
            return;
        }
        validateForm();
    }, [starFreeText, sentencesConfig]);


    return (
        <Box mb={2}>
            <Divider textAlign="left">
                <Typography variant="h6" align="center">Example sentence</Typography>
            </Divider>
            <Box sx={{mb: 1}}/>
            <TextField variant="outlined"
                       fullWidth={true}
                       color="secondary"
                       value={starFreeText}
                       size={"small"}
                       placeholder={`${language} sentence`}
                       spellCheck={false}
                       multiline={true}
                       disabled={isLoading}
                       error={!!sentenceValidationError}
                       helperText={sentenceValidationError}
                       onChange={(e) => onChange([{...sentencesConfig?.[0], sentence: e.target.value}])}
                       InputProps={{
                           style: {fontSize: 14},
                           startAdornment: isLoading ? (
                               <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                   <CircularProgress size={20} color="secondary" />
                               </Box>
                           ) : null,
                       }}
            />
            <Box sx={{mb: 1}}/>
            <TextField variant="outlined"
                       spellCheck={true}
                       multiline={true}
                       autoCapitalize="on"
                       fullWidth={true}
                       color="secondary"
                       value={sentencesConfig?.[0]?.translation || ""}
                       size={"small"}
                       placeholder={"Sentence translation"}
                       disabled={isLoading}
                       error={!!translationValidationError}
                       helperText={translationValidationError}
                       onChange={(e) => onChange([{...sentencesConfig?.[0], translation: e.target.value}])}
                       InputProps={{
                           style: {fontSize: 14},
                           startAdornment: isLoading ? (
                               <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                   <CircularProgress size={20} color="secondary" />
                               </Box>
                           ) : null,
                       }}
            />
            <Box sx={{mb: 1}}/>
            <AudioParamsView type={AudioType.Sentence}
                             text={starFreeText}
                             existingAudioPath={existingAudioPath}
                             audioConfig={sentencesConfig?.[0]?.audio || null}
                             onChange={(audioConfig) => onChange([{...sentencesConfig?.[0], audio: audioConfig}])}
                             isLoading={isLoading}/>
            <Box sx={{mb: 2}}/>
        </Box>
    )

});